import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { StatCard } from '@/components/StatCard'
import { DashboardChart } from '@/components/DashboardChart'
import { fetchAffiliates, fetchAffiliatePayments } from '@/lib/affiliates'
import type { Affiliate, AffiliatePayment } from '@/types/affiliates'
import styles from './AfiliadosDashboardPage.module.css'

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function buildMonthlyChart(payments: AffiliatePayment[]) {
  const map = new Map<string, { vendas: number; comissoes: number }>()

  for (const p of payments) {
    const date = new Date(p.paid_at)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const entry = map.get(key) ?? { vendas: 0, comissoes: 0 }
    entry.vendas += p.payment_amount
    entry.comissoes += p.commission_amount
    map.set(key, entry)
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([key, values]) => {
      const [year, month] = key.split('-')
      return {
        mes: `${MONTH_LABELS[parseInt(month) - 1]}/${year.slice(2)}`,
        vendas: values.vendas,
        comissoes: values.comissoes,
      }
    })
}

export function AfiliadosDashboardPage() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([])
  const [globalSales, setGlobalSales] = useState(0)
  const [globalCommission, setGlobalCommission] = useState(0)
  const [payments, setPayments] = useState<AffiliatePayment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [affiliatesRes, paymentsRes] = await Promise.all([
        fetchAffiliates(),
        fetchAffiliatePayments({ per_page: 100 }),
      ])
      setAffiliates(affiliatesRes.affiliates)
      setGlobalSales(affiliatesRes.global.total_sales)
      setGlobalCommission(affiliatesRes.global.total_commission)
      setPayments(paymentsRes.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados de afiliados.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const totalPaymentsCount = useMemo(
    () => affiliates.reduce((acc, a) => acc + a.metrics.payments_count, 0),
    [affiliates]
  )

  const ticketMedio = useMemo(
    () => (totalPaymentsCount > 0 ? globalSales / totalPaymentsCount : 0),
    [globalSales, totalPaymentsCount]
  )

  const topAfiliados = useMemo(
    () => [...affiliates].sort((a, b) => b.metrics.total_sales - a.metrics.total_sales).slice(0, 5),
    [affiliates]
  )

  const monthlyData = useMemo(() => buildMonthlyChart(payments), [payments])

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        <span>{error}</span>
        <button className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* KPIs linha 1 */}
      <div className={styles.statsGrid}>
        <StatCard
          title="Afiliados Ativos"
          value={affiliates.length}
          icon="👥"
          subtitle="Total"
        />
        <StatCard
          title="Clientes Indicados"
          value={totalPaymentsCount.toLocaleString('pt-BR')}
          icon="🤝"
          subtitle="Total de vendas atribuídas"
        />
        <StatCard
          title="Faturamento Total"
          value={formatCurrency(globalSales)}
          icon="💰"
          subtitle="Via afiliados"
        />
        <StatCard
          title="Comissão Paga"
          value={formatCurrency(globalCommission)}
          icon="💸"
          subtitle="Total gerado"
        />
      </div>

      {/* KPIs linha 2 */}
      <div className={styles.statsGridSecondary}>
        <StatCard
          title="Ticket Médio"
          value={formatCurrency(ticketMedio)}
          icon="🎫"
        />
        <StatCard
          title="Total de Vendas"
          value={totalPaymentsCount.toLocaleString('pt-BR')}
          icon="📊"
          subtitle="Pagamentos pagos"
        />
      </div>

      {/* Gráfico + Top Afiliados */}
      <div className={styles.mainContent}>
        <div className={styles.chartSection}>
          <DashboardChart title="Performance dos Afiliados">
            {monthlyData.length === 0 ? (
              <div className={styles.emptyChart}>
                <Icon icon="mdi:chart-line" />
                <span>Sem dados de vendas para exibir</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,22,54,0.1)" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6c757d' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#6c757d' }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(typeof value === 'number' ? value : 0)}
                    contentStyle={{ background: '#ffffff', border: '1px solid rgba(59,22,54,0.2)', borderRadius: 8 }}
                    labelStyle={{ color: '#212529', fontWeight: 600 }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="vendas" name="Vendas" stroke="#5e17eb" strokeWidth={2} dot={{ fill: '#5e17eb', r: 3 }} />
                  <Line type="monotone" dataKey="comissoes" name="Comissões" stroke="#d4006b" strokeWidth={2} dot={{ fill: '#d4006b', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </DashboardChart>
        </div>

        <div className={styles.topAfiliados}>
          <div className={styles.topHeader}>
            <h3 className={styles.topTitle}>
              <Icon icon="mdi:trophy" className={styles.trophyIcon} />
              Top Afiliados
            </h3>
            <span className={styles.topSubtitle}>Ranking por vendas totais</span>
          </div>

          {topAfiliados.length === 0 ? (
            <div className={styles.emptyTop}>
              <Icon icon="mdi:account-group-outline" />
              <span>Nenhum afiliado com vendas</span>
            </div>
          ) : (
            <ol className={styles.topList}>
              {topAfiliados.map((afiliado, index) => (
                <li key={afiliado.id} className={styles.topItem}>
                  <span className={styles.topRank}>{index + 1}</span>
                  <div className={styles.topInfo}>
                    <span className={styles.topName}>{afiliado.affiliate_name}</span>
                    <span className={styles.topSales}>
                      {afiliado.metrics.payments_count} vendas · {formatCurrency(afiliado.metrics.total_sales)}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

    </div>
  )
}
